import { useState, useEffect, useRef } from 'react'

const heroImages = [
  "/hero/1.png",
  "/hero/2.png",
  "/hero/3.png",
  "/hero/4.png",
]

const Hero = () => {
  const containerRef = useRef(null)
  const [progress, setProgress] = useState(0)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [currentImageIdx, setCurrentImageIdx] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIdx((prev) => (prev + 1) % heroImages.length)
    }, 3500)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const totalHeight = rect.height - windowHeight

      // Calculate scroll progress from 0 (entry) to 1 (exit)
      const scrolled = -rect.top
      const currentProgress = Math.min(Math.max(scrolled / totalHeight, 0), 1)
      setProgress(currentProgress)
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth)
      handleScroll()
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleAdoptScroll = (e) => {
    e.preventDefault()
    const el = document.getElementById('adoption')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Calculate desktop timelines:
  // Phase 1: Paws slide in (progress: 0.15 -> 0.35)
  const pawInProgress = Math.min(Math.max((progress - 0.15) / 0.20, 0), 1)

  // Phase 2 & 3: Synced paws slide out and curtain splits (progress: 0.50 -> 0.85)
  const splitProgress = Math.min(Math.max((progress - 0.50) / 0.35, 0), 1)
  const panelTx = splitProgress * (windowWidth / 2 + 100) // slides panel completely offscreen

  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800
  // On mobile, start sliding up immediately (from progress 0.02 to 0.65) so the scroll doesn't wait
  const splitProgressMobile = Math.min(Math.max((progress - 0.02) / 0.63, 0), 1)
  const panelTyMobile = splitProgressMobile * (windowHeight + 100) // slides panel completely up on mobile
  const isMobile = windowWidth < 1024

  const leftTransform = isMobile 
    ? `translate3d(0, ${-panelTyMobile}px, 0)` 
    : `translate3d(${-panelTx}px, 0, 0)`

  const rightTransform = isMobile 
    ? `translate3d(0, ${-panelTyMobile}px, 0)` 
    : `translate3d(${panelTx}px, 0, 0)`

  const pawOutProgress = splitProgress

  // Static max slide target: paw stays partially offscreen (peeking) even at its peak inward point
  const targetX = -140
  const slideDistance = 400 + targetX

  // Interpolate paw position offset: starts at -400px (fully offscreen), slides in to targetX, holds, slides back to -400px
  let leftPawX = -400
  let pawRotate = 0
  if (progress < 0.50) {
    // Slides in from 0.15 to 0.35 and holds static at targetX from 0.35 to 0.50
    leftPawX = -400 + pawInProgress * slideDistance
    pawRotate = -12 + pawInProgress * 12
  } else {
    // Slides back out from 0.50 to 0.85 synced with the curtain split
    leftPawX = targetX - pawOutProgress * slideDistance
    pawRotate = pawOutProgress * -12
  }

  const rightPawX = -leftPawX
  const leftPawRotate = pawRotate
  const rightPawRotate = -pawRotate

  // Revealing Hero underlay animations (scale and fade in)
  const heroOpacity = Math.min(Math.max((progress - 0.45) / 0.30, 0), 1)
  const heroScale = 0.95 + heroOpacity * 0.05

  // Render unified content inside split halves
  const renderCurtainContent = (side) => {
    const isLeft = side === 'left'

    return (
      <div
        className="absolute top-0 w-full lg:w-[200%] h-full flex flex-col justify-between pt-6 pb-0 lg:pb-0 px-0 bg-brand-orange-500 select-none text-white"
        style={{
          [isLeft ? 'left' : 'right']: 0
        }}
      >
        {/* Header Content */}
        <div className="max-w-4xl 2xl:max-w-5xl mx-auto text-center px-4 pt-10 sm:pt-14 lg:pt-16 2xl:pt-24 shrink-0 relative z-20">
          <span className="inline-block px-3.5 py-1 md:mt-8 bg-white/20 border border-white/30 text-white font-bold text-[10px] sm:text-xs uppercase rounded-full tracking-widest mb-1.5">
            JOIN THE MOVEMENT
          </span>
          <h2
            className="text-3xl xs:text-4xl sm:text-4xl md:text-5xl lg:text-5xl 2xl:text-6xl font-bold text-white leading-tight tracking-wide font-handwritten whitespace-normal sm:whitespace-nowrap mb-1"
            style={{ fontFamily: "'Caveat', cursive" }}
          >
            Be the Voice for the Voiceless
          </h2>
          <p className="text-sm xs:text-base sm:text-lg md:text-xl text-white font-semibold leading-relaxed mt-1">
            Every volunteer helps rescue, protect, and give abandoned animals a second chance at life.
          </p>
        </div>

        {/* Centerpiece Image Layout & Sliding Paws */}
        <div className="relative flex-grow flex items-end justify-center w-full pt-4 pb-0">

          {/* LEFT SIDE: Two paws sliding in / out */}
          <div
            className="absolute left-0 top-[54%] -mt-16 z-20 pointer-events-none hidden md:block"
            style={{ transform: `translate3d(${leftPawX}px, calc(-50% - 90px), 0) rotate(${leftPawRotate}deg)` }}
          >
            <div className="flex flex-col gap-8 items-start">
              {/* Left Paw */}
              <img
                src="/paws1.png"
                alt="Volunteer Paw 2"
                className="w-80 h-80 sm:w-[400px] sm:h-[400px] 2xl:w-[460px] 2xl:h-[460px] shrink-0 object-contain translate-x-20 rotate-12 opacity-100"
              />
            </div>
          </div>

          {/* RIGHT SIDE: Two paws sliding in / out */}
          <div
            className="absolute right-0 top-[54%] -mt-16 z-20 pointer-events-none hidden md:block"
            style={{ transform: `translate3d(${rightPawX}px, calc(-50% - 90px), 0) rotate(${rightPawRotate}deg)` }}
          >
            <div className="flex flex-col gap-8 items-end">
              {/* Right Paw */}
              <img
                src="/pawsleft1.png"
                alt="Volunteer Paw 4"
                className="w-80 h-80 sm:w-[400px] sm:h-[400px] 2xl:w-[460px] 2xl:h-[460px] shrink-0 object-contain -translate-x-20 -rotate-12 opacity-100"
              />
            </div>
          </div>

          {/* Unified centerpiece organic masked image */}
          <div className="absolute bottom-0 inset-x-0 w-full h-[45vh] hidden lg:flex xs:h-[50vh] sm:h-[55vh] lg:h-[380px] xl:h-[460px] 2xl:h-[620px] overflow-hidden items-end pointer-events-none z-10">
            <img
              src="/try1.png"
              alt="Caring Volunteers"
              className="w-full h-full object-cover object-bottom origin-bottom block"
            />
          </div>
           <div className="absolute bottom-0 inset-x-0 w-full h-[65vh] flex lg:hidden overflow-hidden items-end pointer-events-none z-10">
            <img
              src="/try.png"
              alt="Caring Volunteers"
              className="w-full h-full object-cover object-bottom origin-bottom block"
            />
          </div>

        </div>

        {/* Scroll indicator footer */}
        {/* {!isMobile && (
          <div className="w-full flex flex-col items-center gap-1 shrink-0 py-2 text-[10px] text-white/80 font-bold tracking-widest uppercase">
            <span>Scroll to explore</span>
            <svg className="w-4 h-4 text-white animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        )} */}

      </div>
    )
  }

  return (
    <section
      ref={containerRef}
      id="hero"
      className="relative h-[200vh] sm:h-[210vh] lg:h-[220vh] bg-brand-cream-50 scroll-mt-24"
    >
      {/* Sticky Frame viewport container - capped on iPad Pro to avoid 1366px h-screen height */}
      <div className="sticky top-0 h-[100dvh] lg:max-h-[800px] xl:max-h-[900px] 2xl:max-h-none w-full overflow-hidden flex flex-col justify-center">

        {/* REVEALED UNDERLAY: Actual Hero Section Content */}
        <div
          className="absolute inset-0 z-10 flex flex-col justify-between pt-14 sm:pt-20 pb-6 sm:pb-8 bg-[#eae6df] bg-[url('/hero1.png')] bg-cover bg-center bg-no-repeat overflow-hidden"
          style={{
            opacity: 1,
            transform: `scale(${heroScale})`,
            pointerEvents: progress > 0.50 ? 'auto' : 'none'
          }}
        >
          {/* Soft light overlay for text contrast at the left side */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/30 via-white/10 to-transparent pointer-events-none" />

          {/* Main Content Layout */}
          <div className="max-w-8xl mx-auto w-full relative z-20 flex-grow flex flex-col justify-center items-center px-4 sm:px-8 lg:px-12 2xl:px-16 py-2 sm:py-4">
            <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 sm:gap-6 lg:gap-8 2xl:gap-10">

              {/* Top/Left Text Block */}
              <div className="w-full lg:w-1/3 text-center lg:text-left space-y-2 sm:space-y-3 z-20 mt-0">
                <h1
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl 2xl:text-7xl text-[#1e293b] leading-tight tracking-normal"
                  style={{ fontFamily: 'var(--app-font-heading)' }}
                >
                  You can make a difference in their lives
                </h1>
              </div>

              {/* Center Carousel Images Block */}
              <div className="w-full lg:w-1/3 2xl:w-[38%] flex justify-center items-center h-[280px] xs:h-[320px] sm:h-[400px] lg:h-[480px] 2xl:h-[680px] relative z-10 my-1 lg:my-0">
                <div className="relative w-full h-full">
                  {heroImages.map((img, idx) => (
                    <div
                      key={idx}
                      className={`absolute inset-0 flex items-center justify-center transition-opacity duration-1000 ease-in-out ${
                        idx === currentImageIdx ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Rescued animal ${idx + 1}`}
                        width="540"
                        height="540"
                        loading={idx === 0 ? "eager" : "lazy"}
                        fetchPriority={idx === 0 ? "high" : "auto"}
                        decoding="async"
                        className="max-w-full max-h-full object-contain mx-auto scale-105 sm:scale-105 md:scale-110 lg:scale-105 2xl:scale-110"
                      />
                    </div>
                  ))}

                  {/* Dots navigation with accessible 44x44px touch targets */}
                  <div className="absolute -bottom-8 sm:-bottom-6 2xl:-bottom-7 left-1/2 -translate-x-1/2 flex items-center gap-1 z-30 select-none">
                    {heroImages.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setCurrentImageIdx(idx)}
                        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center cursor-pointer focus:outline-none"
                        aria-label={`Go to hero slide ${idx + 1}`}
                      >
                        <span
                          className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 block ${
                            idx === currentImageIdx ? 'bg-brand-orange-500 w-5 sm:w-7' : 'bg-slate-400/60 hover:bg-slate-500 w-2.5 sm:w-3'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom/Right Action & Description Block */}
              <div className="w-full lg:w-1/3 flex flex-col items-center lg:items-end justify-center gap-3 sm:gap-4 2xl:gap-6 z-20 text-center lg:text-right mt-2 sm:mt-4 lg:mt-0">
                <p className="text-sm sm:text-base md:text-lg lg:text-base 2xl:text-lg text-slate-700 leading-relaxed font-semibold max-w-xs sm:max-w-md md:max-w-lg 2xl:max-w-md">
                  Supporting street animals, running veterinary health camps, and building a rabies-free city through coordinated community action.
                </p>
                <div className="flex flex-row lg:flex-col items-center lg:items-end gap-3 sm:gap-3.5 2xl:gap-3.5 w-full sm:w-auto justify-center mt-2 sm:mt-3 lg:mt-0">
                  <a
                    href="#adoption"
                    onClick={handleAdoptScroll}
                    className="px-5 py-2.5 xs:px-6 xs:py-3 sm:px-6 sm:py-3 2xl:px-7 2xl:py-3.5 bg-[#1e293b] hover:bg-[#0f172a] text-white font-extrabold rounded-full text-xs xs:text-sm sm:text-sm md:text-base lg:text-sm shadow-md transition-colors cursor-pointer text-center whitespace-nowrap shrink-0"
                  >
                    Adopt a pet
                  </a>
                  <a
                    href="https://farmvet.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 xs:px-6 xs:py-3 sm:px-6 sm:py-3 2xl:px-6 2xl:py-3.5 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 font-extrabold rounded-full text-xs xs:text-sm sm:text-sm md:text-base lg:text-sm transition-all shadow-sm text-center flex items-center justify-center gap-1.5 cursor-pointer transform hover:-translate-y-0.5 whitespace-nowrap shrink-0"
                  >
                    <span>Consult Vets Online</span>
                    <svg className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 2xl:w-4 2xl:h-4 text-slate-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* LEFT STICKY CURTAIN */}
        <div
          className="absolute left-0 top-0 w-full lg:w-1/2 h-full overflow-hidden z-20"
          style={{
            transform: leftTransform,
            pointerEvents: progress < 0.85 ? 'auto' : 'none'
          }}
        >
          {renderCurtainContent('left')}
        </div>

        {/* RIGHT STICKY CURTAIN */}
        <div
          className="absolute right-0 top-0 w-1/2 h-full overflow-hidden z-20 hidden lg:block"
          style={{
            transform: rightTransform,
            pointerEvents: progress < 0.85 ? 'auto' : 'none'
          }}
        >
          {renderCurtainContent('right')}
        </div>

      </div>
    </section>
  )
}

export default Hero
